import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  alpha,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { TableSkeleton } from './skeletons';
import { EmptyState, SortIcon } from './primitives';
import {
  scoreColor, labelColor, phaseColor, readinessColor,
  fmtScore, fmtPrice, fmtPct,
  thSx, tdSx,
} from './helpers';

// ═══════════════════════════════════════════════════════════════════════════

const LeaderboardBlock = ({ isFirstLoad, filtered, data, sortCol, sortDir, handleSort, openDetail, labelText, t, T }) => (
      (() => {
        if (isFirstLoad) return <TableSkeleton />;
        if (filtered.length === 0) {
          return (
            <EmptyState
              Icon={Search}
              title={data ? T('table.empty') : T('table.startScan')}
            />
          );
        }
        return (
      <TableContainer sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...thSx, position: 'sticky', left: 0, zIndex: 3, bgcolor: '#fafbfc', width: 28 }}>{T('table.rank')}</TableCell>
              <TableCell sx={{ ...thSx, position: 'sticky', left: 28, zIndex: 3, bgcolor: '#fafbfc', cursor: 'pointer' }} onClick={() => handleSort('ticker')}>
                {T('table.ticker')} <SortIcon col="ticker" sortCol={sortCol} sortDir={sortDir} />
              </TableCell>
              <TableCell sx={{ ...thSx, cursor: 'pointer', textAlign: 'center' }} onClick={() => handleSort('TotalAccumBreakScore')}>
                {T('table.totalScore')} <SortIcon col="TotalAccumBreakScore" sortCol={sortCol} sortDir={sortDir} />
              </TableCell>
              <TableCell sx={{ ...thSx, cursor: 'pointer', textAlign: 'center' }} onClick={() => handleSort('AccumulationScore')}>
                {T('table.accumulation')} / {T('table.breakReady')} <SortIcon col="AccumulationScore" sortCol={sortCol} sortDir={sortDir} />
              </TableCell>
              <TableCell sx={thSx}>{T('table.label')} / {T('table.wyckoff')}</TableCell>
              <TableCell sx={{ ...thSx, cursor: 'pointer', textAlign: 'center' }} onClick={() => handleSort('dist_to_pivot')}>
                {T('table.distPivot')} <SortIcon col="dist_to_pivot" sortCol={sortCol} sortDir={sortDir} />
              </TableCell>
              <TableCell sx={{ ...thSx, cursor: 'pointer', textAlign: 'right' }} onClick={() => handleSort('close')}>
                {T('table.price')} / {T('table.entry')} / {T('table.stop')} <SortIcon col="close" sortCol={sortCol} sortDir={sortDir} />
              </TableCell>
              <TableCell sx={{ ...thSx, cursor: 'pointer', textAlign: 'center' }} onClick={() => handleSort('markup_readiness')}>
                {T('table.readiness')} <SortIcon col="markup_readiness" sortCol={sortCol} sortDir={sortDir} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((r, i) => (
              <TableRow
                key={r.ticker}
                onClick={() => openDetail(r.ticker)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { bgcolor: alpha('#6366f1', 0.04) },
                  bgcolor: r.accum_break_label === 'break_imminent' ? alpha('#ef4444', 0.02) : 'transparent',
                }}
              >
                {/* Rank */}
                <TableCell sx={{ ...tdSx, position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1, fontWeight: 600, color: '#94a3b8', width: 28 }}>
                  {i + 1}
                </TableCell>
                {/* Ticker */}
                <TableCell sx={{ ...tdSx, position: 'sticky', left: 28, bgcolor: '#fff', zIndex: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#0f172a', letterSpacing: '0.02em' }}>
                    {r.ticker}
                  </Typography>
                </TableCell>
                {/* Total Score — inline colored number */}
                <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: scoreColor(r.TotalAccumBreakScore) }}>
                    {fmtScore(r.TotalAccumBreakScore)}
                  </Typography>
                </TableCell>
                {/* Accum / Break — two numbers stacked */}
                <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: scoreColor(r.AccumulationScore) }}>
                      {fmtScore(r.AccumulationScore)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#cbd5e1' }}>/</Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: scoreColor(r.BreakReadinessScore) }}>
                      {fmtScore(r.BreakReadinessScore)}
                    </Typography>
                  </Box>
                </TableCell>
                {/* Label + Wyckoff — inline dot + text */}
                <TableCell sx={tdSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, bgcolor: labelColor(r.accum_break_label), boxShadow: `0 0 4px ${labelColor(r.accum_break_label)}` }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: labelColor(r.accum_break_label) }}>
                      {labelText(r.accum_break_label)}
                    </Typography>
                    {r.wyckoff_phase && (
                      <>
                        <Typography sx={{ fontSize: '0.62rem', color: '#cbd5e1', mx: -0.2 }}>·</Typography>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: phaseColor(r.wyckoff_phase) }}>
                          {t(`phase.phases.${r.wyckoff_phase}`, r.wyckoff_phase)}
                        </Typography>
                      </>
                    )}
                  </Box>
                </TableCell>
                {/* Dist to Pivot */}
                <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: (r.dist_to_pivot || 0) <= 3 ? '#10b981' : '#64748b' }}>
                    {fmtPct(r.dist_to_pivot)}
                  </Typography>
                </TableCell>
                {/* Price / Entry / Stop — stacked */}
                <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>
                    {fmtPrice(r.close)}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.8 }}>
                    <Typography sx={{ fontSize: '0.66rem', color: '#10b981', fontWeight: 600 }}>
                      ↑{fmtPrice(r.entry_break_level)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.66rem', color: '#ef4444', fontWeight: 600 }}>
                      ↓{fmtPrice(r.stop_loss_level)}
                    </Typography>
                  </Box>
                </TableCell>
                {/* Readiness — compact */}
                <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                  {r.markup_readiness == null ? (
                    <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>—</Typography>
                  ) : (
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: readinessColor(r.markup_readiness) }}>
                      {(r.markup_readiness * 100).toFixed(0)}%
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
        );
      })()
);
LeaderboardBlock.propTypes = {
  isFirstLoad: PropTypes.bool,
  filtered: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object,
  sortCol: PropTypes.string,
  sortDir: PropTypes.string,
  handleSort: PropTypes.func,
  openDetail: PropTypes.func,
  labelText: PropTypes.func,
  t: PropTypes.func,
  T: PropTypes.func,
};

// ═══════════════════════════════════════════════════════════════════════════
// TopPicksLoadingSkeleton (extracted for cognitive complexity)

export default LeaderboardBlock;
